import React from 'react';
import { apiJson, apiList } from '../api';

/* ====================================================================
   ADMIN COMP REGISTRATION BAR — QR button + pending count on comp home
   ==================================================================== */
function AdminCompRegistrationBar({ compId, onApproved, onRegChange, externalShowRegs, onCloseRegs, showToast }) {
  const [regs, setRegs] = React.useState([]);
  const [showRegs, setShowRegs] = React.useState(false);
  const [processing, setProcessing] = React.useState(null);

  // Open modal from external trigger (settings modal)
  React.useEffect(() => {
    if (externalShowRegs) {
      setShowRegs(true);
      if (onCloseRegs) onCloseRegs();
    }
  }, [externalShowRegs, onCloseRegs]);

  /* Nothing here told anybody anything when it failed. Approving checked
     `if (r.ok)` and had no else at all, and deleting did not check — so the
     spinner stopped, the list came back unchanged, and the button looked
     broken rather than refused. A club at its plan's member limit is a real
     reason for an approval to be turned down, and one worth reading. */
  const say = React.useCallback((msg, type = 'red') => {
    if (showToast) showToast(msg, type);
  }, [showToast]);

  const loadRegs = React.useCallback(async () => {
    try {
      setRegs(await apiList(`/api/registrations/${compId}`));
    } catch (err) {
      say(err.message || 'Could not load the entries for this competition');
    }
  }, [compId, say]);

  React.useEffect(() => { loadRegs(); }, [loadRegs]);

  const pending = regs.filter(r => r.status === 'pending').length;

  const handleStatus = async (regId, status) => {
    setProcessing(regId);
    try {
      await apiJson(`/api/registrations/${compId}/${regId}`, { method: 'PUT', body: { status } });
      loadRegs();
      if (status === 'approved' && onApproved) onApproved();
      if (onRegChange) onRegChange();
      say(status === 'approved' ? 'Entry approved' : 'Entry declined', 'green');
    } catch (err) {
      say(err.message || `Could not mark that entry as ${status}`);
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (regId) => {
    try {
      await apiJson(`/api/registrations/${compId}/${regId}`, { method: 'DELETE' });
      loadRegs();
      if (onRegChange) onRegChange();
      say('Entry removed', 'green');
    } catch (err) {
      say(err.message || 'Could not remove that entry');
    }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        {pending > 0 && (
          <span className="w3-tag w3-round w3-orange w3-tiny" style={{ cursor: 'pointer', padding: '1px 6px' }}
            onClick={() => setShowRegs(true)}>
            {pending} pending
          </span>
        )}
      </div>

      {/* Registration list modal */}
      {showRegs && (
        <div className="w3-modal" style={{ display: 'block' }}>
        <div className="w3-modal-content w3-card-4 w3-round" style={{ maxWidth: '600px' }}>
          <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700 }}><i className="fa fa-list w3-margin-right"></i>Registrations ({regs.length})</span>
            <button className="w3-button w3-round w3-tiny" onClick={() => setShowRegs(false)}><i className="fa fa-times"></i></button>
          </div>
          {/* Pending first */}
          {regs.filter(r => r.status === 'pending').length > 0 && (
            <div className="w3-container" style={{ padding: '8px 16px' }}>
              <p className="w3-small" style={{ margin: '8px 0 4px', fontWeight: 700 }}><i className="fa fa-clock-o w3-text-orange w3-margin-right"></i>Pending Approval</p>
              {regs.filter(r => r.status === 'pending').map(reg => (
                <div key={reg.id} className="w3-padding-small w3-border-bottom" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <strong>{reg.User ? reg.User.name : 'Unknown'}</strong>
                    {reg.User && reg.User.email && <span className="w3-text-grey w3-small"> — {reg.User.email}</span>}
                    {(reg.Team || reg.teamName) && (
                      <span className="w3-tag w3-round w3-theme-l3 w3-small" style={{ marginLeft: '8px', padding: '1px 8px', fontSize: 'var(--gs-text-xs)' }}>
                        Team: {reg.Team ? reg.Team.name : reg.teamName}
                        {reg.Team && <span className="w3-text-orange"> (pending)</span>}
                      </span>
                    )}
                    {reg.Team && reg.Team.Users && reg.Team.Users.length > 0 && (
                      <div className="w3-tiny w3-text-grey" style={{ marginTop: '2px' }}>
                        Members: {reg.Team.Users.map(u => u.name).join(', ')}
                      </div>
                    )}
                    {!reg.Team && reg.teamMembers && reg.teamMembers.length > 0 && (
                      <div className="w3-tiny w3-text-grey" style={{ marginTop: '2px' }}>
                        Members: {reg.teamMembers.map(m => m.name).join(', ')}
                      </div>
                    )}
                    <div className="w3-tiny w3-text-grey">
                      <i className="fa fa-clock-o w3-margin-right"></i>
                      {new Date(reg.createdAt).toLocaleDateString()} {new Date(reg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button className="w3-button w3-round w3-small w3-green"
                      disabled={processing === reg.id}
                      onClick={() => handleStatus(reg.id, 'approved')}>
                      {processing === reg.id ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-check w3-margin-right"></i>Approve</>}
                    </button>
                    <button className="w3-button w3-round w3-small w3-red"
                      disabled={processing === reg.id}
                      onClick={() => handleStatus(reg.id, 'rejected')}>
                      <i className="fa fa-times"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Approved */}
          {regs.filter(r => r.status === 'approved').length > 0 && (
            <div className="w3-container" style={{ padding: '8px 16px' }}>
              <p className="w3-small" style={{ margin: '8px 0 4px', fontWeight: 700 }}><i className="fa fa-check-circle w3-text-green w3-margin-right"></i>Approved</p>
              {regs.filter(r => r.status === 'approved').map(reg => (
                <div key={reg.id} className="w3-padding-small w3-border-bottom" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ minWidth: '150px' }}>
                    <span className="w3-small">{reg.User ? reg.User.name : 'Unknown'}{reg.Team ? ` (${reg.Team.name})` : reg.teamName ? ` (${reg.teamName})` : ''}</span>
                    {reg.Team && reg.Team.Users && reg.Team.Users.length > 0 && (
                      <div className="w3-tiny w3-text-grey">
                        Members: {reg.Team.Users.map(u => u.name).join(', ')}
                      </div>
                    )}
                  </div>
                  <button className="w3-button w3-round w3-tiny w3-text-grey w3-hover-red" onClick={() => handleDelete(reg.id)} title="Remove">
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* Rejected / Withdrawn */}
          {regs.filter(r => r.status === 'rejected' || r.status === 'withdrawn').length > 0 && (
            <div className="w3-container" style={{ padding: '8px 16px 12px' }}>
              <p className="w3-small" style={{ margin: '8px 0 4px', fontWeight: 700 }}><i className="fa fa-ban w3-text-red w3-margin-right"></i>Rejected / Withdrawn</p>
              {regs.filter(r => r.status === 'rejected' || r.status === 'withdrawn').map(reg => (
                <div key={reg.id} className="w3-padding-small w3-border-bottom" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span className="w3-small w3-text-grey">{reg.User ? reg.User.name : 'Unknown'} — <em>{reg.status}</em></span>
                  <button className="w3-button w3-round w3-tiny w3-text-grey w3-hover-red" onClick={() => handleDelete(reg.id)} title="Remove">
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
          {regs.length === 0 && (
            <div className="w3-container w3-padding w3-center w3-text-grey">
              <em>No registrations yet. Share the QR code or registration link.</em>
            </div>
          )}
        </div>
        </div>
      )}

    </>
  );
}

export default AdminCompRegistrationBar;
